import React, { Component } from 'react';
import PropTypes from 'prop-types';
import DisplayScore from './../../../DisplayScore/DisplayScore';
import DisplayScorePlacements from './../../../DisplayScore/constants/DisplayScorePlacements';
import style from './style/bargraph.scss';

/** BarGraph component
 *
 * Horizontal score bar with a fixed set of elements that differ width, and the active one(s) being slightly taller.
 *
 * @example
 * <BarGraph
 *      fill: true // when set to true, it will fill the whole bar up until the element index matching the score
 *      scoreValue={ 1 } // this determines how many elements are filled up
 *      scoreName={ 'scoreName' } // the label of the score, optional, shown right above the actual score
 *      displayScore={ 3 } // score override, shown above the barGraph at the corresponding element matching score
 *      evaluation={ 'evaluation text' } // evaluation to show underneath the score bar
 *      classes={{
 *          barGraph: {
 *              root: rootClass, // for styling the outermost element
 *              fill: filledClass, // for styling the filled barGraph element
 *              blank: blankClass, // for styling the blank barGraph element
 *          },
 *          categoryScore: categoryScoreClass, // for styling the category score (integer)
 *          displayScore: displayScoreClass, // for styling the displayScore (float, positioned above matching element)
 *          scoreName: scoreNameClass // for styling the scoreName label
 *       }}
 * />
 */
export default class BarGraph extends Component {
    constructor(props) {
        super(props);

        this.isBarGraphElementActive = this.isBarGraphElementActive.bind(this);
        this.getFivePointBarGraphElement = this.getFivePointBarGraphElement.bind(this);
    }

    /**
     * Returns whether given index of barGraph element should be marked 'active' (highlighted)
     * @param {number} scoreValue - scoreValue
     * @param {number} elementIndex - the index of the bar graph element
     * @returns {boolean} active
     */
    isBarGraphElementActive(scoreValue, elementIndex) {
        if (scoreValue >= elementIndex) {
            if (this.props.fill) {

                // fill is set and scoreValue is higher than the index of this element, mark it active
                return true;
            }

            // if fill is not set, but the rounded scoreValue matches the index of this element, mark it active (as the only one in the graph)
            return parseInt(scoreValue) === elementIndex;
        }

        // scoreValue is lower than the element index, this element is not active
        return false;
    }

    /**
     * Returns element to be used in bar graph component, with class and dimensions set according to score and index
     * @param {number} elementIndex - the index of the bar graph element
     * @returns {Element} SVG element
     */
    getFivePointBarGraphElement(elementIndex) {
        const { classes, scoreValue } = this.props;
        const xPositions = [0, 25, 63, 119, 157];
        const widths = [25, 37.5, 56.25, 37.5, 25];

        const active = this.isBarGraphElementActive(scoreValue, elementIndex);

        const fillClass = classes && classes.barGraph && classes.barGraph.fill
            ? `${style.fill} ${classes.barGraph.fill}`
            : style.fill;

        const blankClass = classes && classes.barGraph && classes.barGraph.blank
            ? `${style.blank} ${classes.barGraph.blank}`
            : style.blank;

        return <rect
            key={ `element-${elementIndex}` }
            className={ active ? fillClass : blankClass }
            x={ xPositions[elementIndex - 1] }
            y={ active ? 0 : 2 }
            width={ widths[elementIndex - 1] }
            height={ active ? 11 : 8 }
            rx="4"
        />
    }

    /**
     * Returns the elements that make up a five-point bar graph
     * @returns {Array} array of SVG elements
     */
    getFivePointBarGraph() {
        const barGraph = [];

        // range is 1 to 5
        for (let index = 1; index <= 5; index++) {
            barGraph.push(this.getFivePointBarGraphElement(index));
        }

        return barGraph;
    }

    render() {
        const {
            scoreValue,
            scoreName,
            displayScore,
            classes,
            evaluation
        } = this.props;

        // construct classes for styling the outermost container
        const rootClass = classes && classes.barGraph && classes.barGraph.root
            ? `${style.barGraph} ${classes.barGraph.root}`
            : style.barGraph;

        // construct classes for styling the category score
        const categoryScoreClass = classes && classes.categoryScore
            ? `${style.categoryScore} ${classes.categoryScore}`
            : style.categoryScore;

        return (
            <section className={ rootClass }>
                { scoreName ? <h3 className={ `${style.scoreName} ${classes.scoreName}` }>{ scoreName }</h3> : null }
                { displayScore
                    ? <DisplayScore
                        score={ displayScore }
                        offset={ scoreValue }
                        placement={ DisplayScorePlacements.FIVE_POINT_BAR_GRAPH }
                        classes={{ root: classes.displayScore }}
                    />
                    : <div className={ categoryScoreClass }>{ scoreValue }</div>
                }
                <svg viewBox="0 0 182 16">
                    { this.getFivePointBarGraph() }
                </svg>
                { evaluation ? <span className={ style.evaluation } dangerouslySetInnerHTML={{ __html: evaluation }}/> : null }
            </section>
        );
    }
}

BarGraph.propTypes = {
    fill: PropTypes.bool,
    scoreValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    scoreName: PropTypes.string,
    displayScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    evaluation: PropTypes.string,
    classes: PropTypes.shape({
        barGraph: PropTypes.shape({
            root: PropTypes.string,
            fill: PropTypes.string,
            blank: PropTypes.string
        }),
        categoryScore: PropTypes.string,
        displayScore: PropTypes.string,
        scoreName: PropTypes.string
    })
};

BarGraph.defaultProps = {
    fill: false,
    classes: {
        barGraph: {
            root: '',
            fill: '',
            blank: ''
        },
        categoryScore: '',
        displayScore: '',
        scoreName: ''
    }
};
